import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import type { tComposerNoBalanceBarProps } from './ComposerNoBalanceBar.types';

/**
 * Aviso + bloqueo de saldo insuficiente en el compositor de la conversación
 * 1 a 1 (RET-1806, DS-03 §5; forma del prototipo aplicada en RET-2232).
 *
 * No es un modal: vive INLINE, junto al control de envío que la disparó — no
 * tapa la conversación. Un solo componente cubre los caminos en alcance
 * (plantilla sin variables, con variables, y el texto libre del compositor):
 * mismos testids, mismo contrato.
 *
 * FORMA (prototipo, `.nosaldo-bar`): icono de alerta, título en negrita que
 * dice QUÉ pasó, una sola frase debajo que dice CUÁNTO cuesta y QUÉ hacer, y
 * el botón en su propia línea. Apilado, no en fila: el aviso se lee de
 * corrido antes de llegar a la acción.
 *
 * EL DETALLE ES UNA SOLA FRASE, Y CAMBIA CON EL ROL. La versión anterior
 * partía la información en dos piezas —«Costo de este mensaje: $X» por un
 * lado y «Pídele a un Owner…» por otro, en extremos opuestos de la fila— y
 * como el título además traía «recarga para seguir», al Seller le salían en
 * pantalla «sin saldo» dos veces y una orden que no puede obedecer, al lado
 * del texto que le dice que se lo pida a alguien más. El prototipo tiene UN
 * mensaje por rol (`.nb-oa` / `.nb-seller`), no un tronco común con
 * coletilla, y el precio vive dentro de esa frase: «cuesta X **y no te
 * alcanza**» explica por qué está bloqueado, que es lo que el número suelto
 * no decía.
 *
 * El rol NO se lee aquí — mismo patrón que `LowBalanceBanner` (DS-16/DS-17):
 * quien monta la barra decide si pasa `onTopUp`, porque es quien conoce el
 * rol. Ausente = Seller/Viewer: sin botón (el prototipo esconde
 * `.nb-actions` entero para ese rol) y con la frase que manda a pedirlo.
 * Presente = Owner/Admin: el único CTA (`DS-22`), que abre la recarga con el
 * monto prellenado. DS-03 (ajuste del 10-ago) retira explícitamente el nudge
 * de recarga automática de este estado.
 *
 * Sin número de saldo disponible (DS-03: es muy dinámico con varios sellers
 * a la vez) — sólo el costo de ESTE envío.
 */
export default function ComposerNoBalanceBar(
  props: tComposerNoBalanceBarProps,
) {
  const { costLabel, onTopUp, variant = 'template', recipientName } = props;
  const { t } = useTranslation('wallet');

  const isMessage = variant === 'message';
  const canTopUp = Boolean(onTopUp);

  const title = isMessage
    ? recipientName
      ? t('composer.noBalance.titleMessage', { name: recipientName })
      : t('composer.noBalance.titleMessageNoName')
    : t('composer.noBalance.titleTemplate');

  const detailKey = isMessage
    ? canTopUp
      ? 'composer.noBalance.detailOwner'
      : 'composer.noBalance.detailSeller'
    : canTopUp
      ? 'composer.noBalance.detailTemplateOwner'
      : 'composer.noBalance.detailTemplateSeller';

  return (
    <div
      data-testid="wallet-composer-no-balance-bar"
      role="status"
      className="flex flex-col gap-2.5 rounded-xl border border-error/40 bg-error/10 px-4 py-3 text-base-content"
    >
      <div className="flex items-start gap-2.5">
        <ExclamationTriangleIcon
          aria-hidden="true"
          className="mt-0.5 h-[17px] w-[17px] shrink-0 text-error"
        />
        <p className="flex-1 text-[13px] font-bold leading-[1.4] text-error">
          {title}
          <span
            data-testid={
              canTopUp
                ? 'wallet-composer-no-balance-detail'
                : 'wallet-composer-ask-admin-info'
            }
            className="mt-[3px] block font-normal opacity-80"
          >
            {t(detailKey, { cost: costLabel })}
          </span>
        </p>
      </div>
      {onTopUp ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="wallet-composer-topup-cta"
            className="btn btn-primary btn-sm shrink-0"
            onClick={onTopUp}
          >
            {t('composer.noBalance.cta')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
